import React, { useCallback, useEffect, useMemo, useState } from 'react';
import _ from 'lodash';

import { CircularProgress, FormControl, FormHelperText, Grid, InputLabel } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { Field } from 'formik';
import generalEndpoints from 'app/api/services/generalEndpoints';
import { useSelector } from 'react-redux';
import { SERVICES_CODES } from 'app/config/services';
import HighlightableTextField from '../HighlightableTextField';
import Skeleton from '../Skeleton';
import HighlightableSelect from '../HighlightableSelect';
import { STATUS_CODES } from '../../../config/status';
import { labelObrigatoriedade } from 'app/utils/helpers';
import { DETRAN_KEYS } from '../../../store/slices/vehicle';

function VehicleData(props) {
    const {
        readOnly,
        classes,
        loadingDetran,
        foundOnDetran,
        blockChangesField,
        handleChangeField,
        handleBlurField,
        handleFocusField,
        values,
        errors,
        setFieldValue,
        contextProps: { isInternalForm, canEdit },
        campoDiferenteDoOriginal,
        highlightEnabled,
        isFieldDisabled,
        requiredFields,
    } = props;

    const vehicleCategories = useSelector((state) => state.vehicle.vehicleCategories);
    const vehicleBrands = useSelector((state) => state.vehicle.vehicleBrands);
    const fuelType = useSelector((state) => state.vehicle.fuelType);
    const vehicleModels = useSelector((state) => state.vehicle.vehicleModels);

    const service = useSelector((state) => state.services.details);
    const request = useSelector((state) => state.requests.details);

    const isAnaliseSolicitacao = useMemo(() => {
        return request.cdSituacao === STATUS_CODES.EM_ANALISE;
    }, [request]);
    const isInclusaoVeiculo = useMemo(() => {
        return (
            service.cdServico === SERVICES_CODES.INCLUSAO_VEICULOS ||
            service.cdServico === SERVICES_CODES.INCLUSAO_VEICULOS_SEM_CRC
        );
    }, [service]);

    const [loadingCity, setLoadingCity] = useState(false);
    const [cities, setCities] = useState([]);
    const [city, setCity] = useState(values.municipio || {});
    const [onlyBAState] = useState(true);
    const [canEditPlate] = useState(() => {
        if (isInternalForm && isAnaliseSolicitacao && isInclusaoVeiculo) return true;
        return !isInternalForm;
    });
    const [canEditRenavam] = useState(() => {
        return !(isInternalForm && isAnaliseSolicitacao && isInclusaoVeiculo);
    });
    const [vehicleModelsFiltered, setVehicleModelsFiltered] = useState(vehicleModels);

    const allSgUf = [
        'AC',
        'AL',
        'AM',
        'AP',
        'BA',
        'CE',
        'DF',
        'ES',
        'GO',
        'MA',
        'MG',
        'MS',
        'MT',
        'PA',
        'PB',
        'PE',
        'PI',
        'PR',
        'RJ',
        'RN',
        'RO',
        'RR',
        'RS',
        'SC',
        'SE',
        'SP',
        'TO',
    ];

    const filterModels = useCallback(
        (brandId) => {
            return vehicleModels.filter((model) => {
                return model.cdMarca === Number(brandId);
            });
        },
        [vehicleModels],
    );

    const handleBrand = (brandId, setFieldValue) => {
        const temp = filterModels(brandId);
        setVehicleModelsFiltered(temp);
        setFieldValue('cdMarca', brandId);
        setFieldValue('cdModeloVeiculo', '');
    };

    const searchCity = async (uf, setFieldValue) => {
        setLoadingCity(true);
        if (uf && uf !== '') {
            const oldUf = values.uf || values.municipio?.uf?.sgUf || uf;
            if (uf !== oldUf && setFieldValue != null) {
                setCity(null);
                if (setFieldValue) {
                    setFieldValue('cdMunicipio', '');
                    setFieldValue('municipio', null);
                }
            }
            const response = await generalEndpoints.getCities(uf);
            if (response && response.status === 200 && response.data) {
                setCities(response.data);
                setLoadingCity(false);
                return response.data;
            }
        }
        setLoadingCity(false);
    };

    const onChangeMunicipio = async (value, setFieldValue) => {
        let temp = {};
        let cdMunicipio = null;
        if (value) {
            temp = { ...value };
            cdMunicipio = temp.cdMunicipio;
        }
        setFieldValue('cdMunicipio', cdMunicipio);
        setFieldValue('municipio', temp);
        setCity(value);
    };

    const resultDetran = (elem) => {
        return foundOnDetran.includes(elem);
    };

    const loadingDetranIncludesKey = (elem) => {
        return loadingDetran && DETRAN_KEYS.includes(elem);
    };

    useEffect(() => {
        if (values.municipio && Object.keys(values.municipio).length > 0) {
            if (values.municipio?.uf?.sgUf) {
                searchCity(values.municipio.uf.sgUf);
            }
        } else {
            setCity({});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadingDetran, values?.municipio?.uf?.sgUf]);

    useEffect(() => {
        if (values.municipio && Object.keys(values.municipio).length > 0) {
            if (values.municipio.cdMunicipio !== city.cdMunicipio) {
                setCity(values.municipio);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values.municipio]);

    useEffect(() => {
        if (values.cdMarca) {
            setVehicleModelsFiltered(filterModels(values.cdMarca));
        }
    }, [filterModels, values.cdMarca]);

    return (
        <>
            <Grid item xs={12} sm={4}>
                <Skeleton shouldDisplayChildren={!loadingDetran} transparent>
                    {!canEditPlate ? (
                        <Field
                            as={HighlightableTextField}
                            name="dePlaca"
                            fullWidth
                            disabled
                            id="filled-disabled"
                            label={`Placa${labelObrigatoriedade('dePlaca', requiredFields)}`}
                            margin="dense"
                            variant="filled"
                            safeToHighlight={highlightEnabled}
                            isHighlightedFunction={campoDiferenteDoOriginal('dePlaca')}
                        />
                    ) : (
                        <Field
                            name="dePlaca"
                            label={`Placa${labelObrigatoriedade('dePlaca', requiredFields)}`}
                            variant="filled"
                            margin="dense"
                            disabled={isFieldDisabled('dePlaca') || loadingDetranIncludesKey('dePlaca')}
                            fullWidth
                            error={!!errors.dePlaca}
                            onChange={(e) => handleChangeField(e, setFieldValue)}
                            onBlur={(e) => handleBlurField(e, setFieldValue, values)}
                            onFocus={handleFocusField}
                            helperText={errors.dePlaca ? errors.dePlaca : ''}
                            as={HighlightableTextField}
                            safeToHighlight={highlightEnabled}
                            isHighlightedFunction={campoDiferenteDoOriginal('dePlaca')}
                            inputProps={{
                                maxLength: 7,
                            }}
                        />
                    )}
                </Skeleton>
            </Grid>
            <Grid item xs={12} sm={4}>
                <Skeleton shouldDisplayChildren={!loadingDetran} transparent>
                    {!canEditRenavam ? (
                        <Field
                            as={HighlightableTextField}
                            name="deRenavam"
                            fullWidth
                            disabled
                            id="filled-disabled"
                            label={`Renavam${labelObrigatoriedade('deRenavam', requiredFields)}`}
                            margin="dense"
                            variant="filled"
                            safeToHighlight={highlightEnabled}
                            isHighlightedFunction={campoDiferenteDoOriginal('dePlaca')}
                        />
                    ) : (
                        <Field
                            name="deRenavam"
                            label={`Renavam${labelObrigatoriedade('deRenavam', requiredFields)}`}
                            variant="filled"
                            margin="dense"
                            disabled={
                                isFieldDisabled('deRenavam') ||
                                (resultDetran('deRenavam') && values.deRenavam !== '') ||
                                blockChangesField ||
                                loadingDetranIncludesKey('deRenavam')
                            }
                            onChange={(e) => handleChangeField(e, setFieldValue)}
                            onBlur={(e) => handleBlurField(e, setFieldValue, values)}
                            onFocus={handleFocusField}
                            fullWidth
                            error={!!errors.deRenavam}
                            helperText={errors.deRenavam ? errors.deRenavam : ''}
                            as={HighlightableTextField}
                            safeToHighlight={highlightEnabled}
                            isHighlightedFunction={campoDiferenteDoOriginal('deRenavam')}
                            inputProps={{
                                maxLength: 11,
                            }}
                        />
                    )}
                </Skeleton>
            </Grid>
            <Grid item xs={12} sm={4}>
                <Skeleton shouldDisplayChildren={!loadingDetran} transparent>
                    <Field
                        name="deChassi"
                        label={`Nº Chassi${labelObrigatoriedade('deChassi', requiredFields)}`}
                        variant="filled"
                        margin="dense"
                        disabled={
                            isFieldDisabled('deChassi') ||
                            (resultDetran('deChassi') && values.deChassi !== '') ||
                            blockChangesField ||
                            loadingDetranIncludesKey('deChassi')
                        }
                        fullWidth
                        error={!!errors.deChassi}
                        helperText={errors.deChassi ? errors.deChassi : ''}
                        as={HighlightableTextField}
                        safeToHighlight={highlightEnabled}
                        isHighlightedFunction={campoDiferenteDoOriginal('deChassi')}
                        inputProps={{
                            maxLength: 17,
                        }}
                    />
                </Skeleton>
            </Grid>
            <Grid item xs={12} sm={4}>
                <Skeleton shouldDisplayChildren={true}>
                    {' '}
                    {/* correção rápida da margem... */}
                    <Field
                        name="nuAnoIpva"
                        label={`Exercício do IPVA${labelObrigatoriedade('nuAnoIpva', requiredFields)}`}
                        variant="filled"
                        margin="dense"
                        disabled={
                            isFieldDisabled('nuAnoIpva') ||
                            (resultDetran('nuAnoIpva') && values.nuAnoIpva !== '') ||
                            !canEdit ||
                            blockChangesField
                        }
                        fullWidth
                        error={!!errors.nuAnoIpva}
                        helperText={errors.nuAnoIpva ? errors.nuAnoIpva : ''}
                        as={HighlightableTextField}
                        safeToHighlight={highlightEnabled}
                        isHighlightedFunction={campoDiferenteDoOriginal('nuAnoIpva')}
                        inputProps={{
                            maxLength: 4,
                        }}
                    />
                </Skeleton>
            </Grid>
            <Grid item xs={12} sm={4}>
                <Skeleton shouldDisplayChildren={!loadingDetran} transparent>
                    <Field
                        name="nuAnoFabricacao"
                        label={`Ano Fabricação${labelObrigatoriedade('nuAnoFabricacao', requiredFields)}`}
                        variant="filled"
                        margin="dense"
                        disabled={
                            isFieldDisabled('nuAnoFabricacao') ||
                            (resultDetran('nuAnoFabricacao') && values.nuAnoFabricacao !== '') ||
                            blockChangesField ||
                            loadingDetranIncludesKey('nuAnoFabricacao')
                        }
                        fullWidth
                        error={!!errors.nuAnoFabricacao}
                        helperText={errors.nuAnoFabricacao ? errors.nuAnoFabricacao : ''}
                        as={HighlightableTextField}
                        safeToHighlight={highlightEnabled}
                        isHighlightedFunction={campoDiferenteDoOriginal('nuAnoFabricacao')}
                        inputProps={{
                            maxLength: 4,
                        }}
                    />
                </Skeleton>
            </Grid>
            <Grid item xs={12} sm={4}>
                <Skeleton shouldDisplayChildren={!loadingDetran} transparent>
                    <Field
                        name="nuAnoModelo"
                        label={`Ano Modelo${labelObrigatoriedade('nuAnoModelo', requiredFields)}`}
                        variant="filled"
                        margin="dense"
                        disabled={
                            isFieldDisabled('nuAnoModelo') ||
                            (resultDetran('nuAnoModelo') && values.nuAnoModelo !== '') ||
                            blockChangesField ||
                            loadingDetranIncludesKey('nuAnoModelo')
                        }
                        fullWidth
                        error={!!errors.nuAnoModelo}
                        helperText={errors.nuAnoModelo ? errors.nuAnoModelo : ''}
                        as={HighlightableTextField}
                        safeToHighlight={highlightEnabled}
                        isHighlightedFunction={campoDiferenteDoOriginal('nuAnoModelo')}
                        inputProps={{
                            maxLength: 4,
                        }}
                    />
                </Skeleton>
            </Grid>
            <Grid item xs={12} sm={4}>
                <Skeleton shouldDisplayChildren={!loadingDetran} transparent>
                    <FormControl
                        variant="filled"
                        margin="dense"
                        disabled={
                            isFieldDisabled('cdMarca') ||
                            (resultDetran('cdMarca') && values.cdMarca !== '') ||
                            blockChangesField ||
                            loadingDetranIncludesKey('cdMarca')
                        }
                        fullWidth
                        error={!!errors.cdMarca}
                        onChange={(e) => handleBrand(e.target.value, setFieldValue)}>
                        <InputLabel
                            htmlFor="cdMarca"
                            shrink={true}>{`Marca${labelObrigatoriedade('cdMarca', requiredFields)}`}</InputLabel>
                        <Field
                            name="cdMarca"
                            id="cdMarca"
                            native
                            fullWidth
                            as={HighlightableSelect}
                            isHighlightedFunction={campoDiferenteDoOriginal('cdMarca')}
                            safeToHighlight={highlightEnabled}>
                            <option aria-label="None" value="" />
                            {_.orderBy(vehicleBrands, ['deMarca']).map((elem, index) => {
                                return (
                                    <option key={`type-${index}`} value={elem.cdMarca}>
                                        {elem.deMarca}
                                    </option>
                                );
                            })}
                        </Field>
                        {errors.cdMarca ? <FormHelperText>{errors.cdMarca}</FormHelperText> : null}
                    </FormControl>
                </Skeleton>
            </Grid>
            <Grid item xs={12} sm={4}>
                <Skeleton shouldDisplayChildren={!loadingDetran} transparent>
                    <FormControl
                        variant="filled"
                        margin="dense"
                        disabled={
                            isFieldDisabled('cdModeloVeiculo') ||
                            (resultDetran('cdModeloVeiculo') && values.cdModeloVeiculo !== '') ||
                            blockChangesField ||
                            loadingDetranIncludesKey('cdModeloVeiculo')
                        }
                        fullWidth
                        error={!!errors.cdModeloVeiculo}>
                        <InputLabel
                            htmlFor="cdModeloVeiculo"
                            shrink={
                                true
                            }>{`Modelo${labelObrigatoriedade('cdModeloVeiculo', requiredFields)}`}</InputLabel>
                        <Field
                            name="cdModeloVeiculo"
                            id="cdModeloVeiculo"
                            native
                            fullWidth
                            as={HighlightableSelect}
                            isHighlightedFunction={campoDiferenteDoOriginal('cdModeloVeiculo')}
                            safeToHighlight={highlightEnabled}>
                            <option aria-label="None" value="" />
                            {_.orderBy(vehicleModelsFiltered, ['deModelo']).map((elem, index) => {
                                return (
                                    <option key={`type-${index}`} value={elem.cdModeloVeiculo}>
                                        {elem.deModelo}
                                    </option>
                                );
                            })}
                        </Field>
                        {errors.cdModeloVeiculo ? <FormHelperText>{errors.cdModeloVeiculo}</FormHelperText> : null}
                    </FormControl>
                </Skeleton>
            </Grid>
            <Grid item xs={12} sm={4}>
                <Skeleton shouldDisplayChildren={!loadingDetran} transparent>
                    <FormControl
                        variant="filled"
                        margin="dense"
                        disabled={
                            isFieldDisabled('cdCategoriaVeiculo') ||
                            (resultDetran('cdCategoriaVeiculo') && values.cdCategoriaVeiculo !== '') ||
                            loadingDetranIncludesKey('cdCategoriaVeiculo')
                        }
                        fullWidth
                        error={!!errors.cdCategoriaVeiculo}>
                        <InputLabel htmlFor="cdCategoriaVeiculo">{`Categoria${labelObrigatoriedade('cdCategoriaVeiculo', requiredFields)}`}</InputLabel>
                        <Field
                            name="cdCategoriaVeiculo"
                            id="cdCategoriaVeiculo"
                            native
                            fullWidth
                            as={HighlightableSelect}
                            isHighlightedFunction={campoDiferenteDoOriginal('cdCategoriaVeiculo')}
                            safeToHighlight={highlightEnabled}>
                            <option aria-label="None" value="" />
                            {vehicleCategories.map((elem, index) => {
                                return (
                                    <option key={`type-${index}`} value={elem.cdCategoriaVeiculo}>
                                        {elem.deCategoriaVeiculo}
                                    </option>
                                );
                            })}
                        </Field>
                        {errors.cdCategoriaVeiculo ? (
                            <FormHelperText>{errors.cdCategoriaVeiculo}</FormHelperText>
                        ) : null}
                    </FormControl>
                </Skeleton>
            </Grid>
            <Grid item xs={12} sm={4}>
                <Skeleton shouldDisplayChildren={!loadingDetran} transparent>
                    <FormControl
                        variant="filled"
                        margin="dense"
                        disabled={
                            isFieldDisabled('cdTipoCombustivel') ||
                            (resultDetran('cdTipoCombustivel') && values.cdTipoCombustivel !== '') ||
                            blockChangesField ||
                            loadingDetranIncludesKey('cdTipoCombustivel')
                        }
                        fullWidth
                        error={!!errors.cdTipoCombustivel}>
                        <InputLabel htmlFor="cdTipoCombustivel">{`Combustível${labelObrigatoriedade('cdTipoCombustivel', requiredFields)}`}</InputLabel>
                        <Field
                            name="cdTipoCombustivel"
                            id="cdTipoCombustivel"
                            native
                            fullWidth
                            as={HighlightableSelect}
                            isHighlightedFunction={campoDiferenteDoOriginal('cdTipoCombustivel')}
                            safeToHighlight={highlightEnabled}>
                            <option aria-label="None" value="" />
                            {fuelType.map((elem, index) => {
                                return (
                                    <option key={`comb-${index}`} value={elem.cdTipoCombustivel}>
                                        {elem.deTipoCombustivel}
                                    </option>
                                );
                            })}
                        </Field>
                        {errors.cdTipoCombustivel ? <FormHelperText>{errors.cdTipoCombustivel}</FormHelperText> : null}
                    </FormControl>
                </Skeleton>
            </Grid>
            <Grid item xs={12} sm={2}>
                <Skeleton shouldDisplayChildren={!loadingDetran} transparent>
                    <FormControl
                        variant="filled"
                        margin="dense"
                        disabled={
                            isFieldDisabled('uf') ||
                            (resultDetran('uf') && values.uf !== '') ||
                            loadingDetranIncludesKey('uf')
                        }
                        fullWidth
                        error={!!errors.uf}>
                        <InputLabel htmlFor="uf">{`UF${labelObrigatoriedade('uf', requiredFields)}`}</InputLabel>
                        <Field
                            name="uf"
                            id="uf"
                            onBlur={(e) => searchCity(e.target.value, setFieldValue)}
                            value={values.uf ? values.uf : values?.municipio?.uf?.sgUf ? values.municipio.uf.sgUf : ''}
                            native
                            fullWidth
                            as={HighlightableSelect}
                            isHighlightedFunction={campoDiferenteDoOriginal('municipio.uf.sgUf')}
                            safeToHighlight={highlightEnabled}>
                            <option aria-label="None" value="" />
                            {onlyBAState ? (
                                <option value="BA">BA</option>
                            ) : (
                                allSgUf.map((uf) => <option value={uf}>{uf}</option>)
                            )}
                        </Field>
                        {errors.uf ? <FormHelperText>{errors.uf}</FormHelperText> : null}
                    </FormControl>
                </Skeleton>
            </Grid>
            <Grid item xs={12} sm={6}>
                <Skeleton shouldDisplayChildren={!loadingDetran} transparent>
                    <Field name="municipio">
                        {({ field, form: { errors } }) => {
                            return (
                                <div style={{ width: '100%' }} className={classes.positionRelative}>
                                    <Autocomplete
                                        label={`Município${labelObrigatoriedade('municipio', requiredFields)}`}
                                        id="municipio"
                                        options={cities}
                                        getOptionLabel={(option) =>
                                            option['nmMunicipio'] ? option['nmMunicipio'] : ''
                                        }
                                        disabled={
                                            isFieldDisabled('municipio') ||
                                            (resultDetran('cdMunicipio') && values.municipio !== '') ||
                                            blockChangesField ||
                                            loadingDetranIncludesKey('municipio')
                                        }
                                        onChange={(e, v) => onChangeMunicipio(v, setFieldValue)}
                                        value={city}
                                        renderInput={(params) => (
                                            <HighlightableTextField
                                                {...params}
                                                label={`Município${labelObrigatoriedade('municipio', requiredFields)}`}
                                                fullWidth
                                                variant="filled"
                                                margin="dense"
                                                error={!!errors.municipio}
                                                value={city?.nmMunicipio}
                                                safeToHighlight={highlightEnabled}
                                                isHighlightedFunction={campoDiferenteDoOriginal(
                                                    'municipio.nmMunicipio',
                                                )}
                                                helperText={
                                                    errors?.municipio?.cdMunicipio ? errors.municipio.cdMunicipio : ''
                                                }
                                            />
                                        )}
                                    />
                                    {loadingCity && (
                                        <CircularProgress
                                            className={classes.circularProgress}
                                            disableShrink={true}
                                            size={18}
                                            thickness={6}
                                        />
                                    )}
                                </div>
                            );
                        }}
                    </Field>
                </Skeleton>
            </Grid>
            <Grid item xs={12} sm={4}>
                <Skeleton shouldDisplayChildren={!loadingDetran} transparent>
                    <Field
                        name="vlPotenciaMotor"
                        label={`Potência do Motor${labelObrigatoriedade('vlPotenciaMotor', requiredFields)}`}
                        variant="filled"
                        margin="dense"
                        disabled={
                            isFieldDisabled('vlPotenciaMotor') ||
                            (resultDetran('vlPotenciaMotor') && values.vlPotenciaMotor !== '') ||
                            blockChangesField ||
                            loadingDetranIncludesKey('vlPotenciaMotor')
                        }
                        fullWidth
                        error={!!errors.vlPotenciaMotor}
                        helperText={errors.vlPotenciaMotor ? errors.vlPotenciaMotor : ''}
                        as={HighlightableTextField}
                        safeToHighlight={highlightEnabled}
                        isHighlightedFunction={campoDiferenteDoOriginal('vlPotenciaMotor')}
                        inputProps={{
                            maxLength: 3,
                            form: {
                                autoComplete: 'off',
                            },
                        }}
                    />
                </Skeleton>
            </Grid>
            <Grid item xs={12} sm={4}>
                <Field
                    name="nuAgerba"
                    label={`Número AGERBA${labelObrigatoriedade('nuAgerba', requiredFields)}`}
                    variant="filled"
                    margin="dense"
                    disabled={
                        readOnly ||
                        (isInternalForm &&
                        request.cdSituacao === STATUS_CODES.EM_ANALISE &&
                        service?.cdServico !== SERVICES_CODES.EXCLUSAO_VEICULOS
                            ? false
                            : isFieldDisabled('nuAgerba') || loadingDetranIncludesKey('nuAgerba'))
                    }
                    fullWidth
                    error={!!errors.nuAgerba}
                    helperText={errors.nuAgerba ? errors.nuAgerba : ''}
                    as={HighlightableTextField}
                    safeToHighlight={highlightEnabled}
                    isHighlightedFunction={campoDiferenteDoOriginal('nuAgerba')}
                    inputProps={{
                        maxLength: 10,
                    }}
                    style={{ margin: '0' }}
                />
            </Grid>
            <Grid item xs={12} sm={4}>
                <Field
                    name="nuOrdemEmpresa"
                    label={`Nº Ordem Empresa${labelObrigatoriedade('nuOrdemEmpresa', requiredFields)}`}
                    variant="filled"
                    margin="dense"
                    disabled={isFieldDisabled('nuOrdemEmpresa') || loadingDetranIncludesKey('')}
                    fullWidth
                    error={!!errors.nuOrdemEmpresa}
                    helperText={errors.nuOrdemEmpresa ? errors.nuOrdemEmpresa : ''}
                    as={HighlightableTextField}
                    safeToHighlight={highlightEnabled}
                    isHighlightedFunction={campoDiferenteDoOriginal('nuOrdemEmpresa')}
                    inputProps={{
                        maxLength: 10,
                    }}
                    style={{ margin: '0' }}
                />
            </Grid>
        </>
    );
}

export default VehicleData;
